import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';

export type TourSection =
  | 'dashboard-no-subjects'
  | 'add-subject'
  | 'dashboard-with-subjects'
  | 'child-no-subjects'
  | 'child-with-subjects'
  | 'parent-no-children'
  | 'parent-with-children'
  | 'lesson-general'
  | 'lesson-voice'
  | 'lesson-pdf-context';

export type TourContext = 'dashboard' | 'add-subject' | 'lesson';

export interface TourStepConfig {
  id: string;
  context: TourContext;
  section: TourSection;
  attachTo?: {
    element: string;
    on: Shepherd.PopperPlacement;
  };
  title: string;
  text: string;
  buttons: Array<{
    text: string;
    classes?: string;
    action?: any; // Shepherd binds its own context to these functions
  }>;
  advanceOn?: any;
  when?: any;
  mobileOnly?: boolean; // If true, this step will only show on mobile devices (width < 768px)
  mobileConfig?: {
    modalWidth?: number; // Custom modal width (default: 280px)
    arrowSpacing?: number; // Distance between arrow and modal (default: 60px)
    modalOffset?: { top?: number; left?: number }; // Additional offset for modal position
    arrowOffset?: { top?: number; left?: number }; // Additional offset for arrow position
    delayPositioning?: number; // Delay positioning in ms (for steps that trigger UI changes like opening menus)
    keepDefaultTop?: boolean; // If true, don't override Shepherd's default top positioning
    keepDefaultLeft?: boolean; // If true, don't override Shepherd's default left positioning
    keepDefaultTransform?: boolean; // If true, don't override Shepherd's default transform
  };
}

// Helper function to get responsive text based on screen size
export function getResponsiveText(mobileText: string, desktopText?: string): string {
  const isMobile = window.innerWidth < 768;
  return isMobile ? mobileText : (desktopText || mobileText);
}

// Custom theme for dark mode
export const tourTheme = `
  .shepherd-element {
    background: #111827;
    border: 1px solid #4b5563;
    border-radius: 8px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.4);
    max-width: 400px;
  }

  /* Mobile-specific compact styles */
  @media (max-width: 767px) {
    .shepherd-element {
      max-width: 280px !important;
      position: fixed !important;
    }

    .shepherd-header {
      padding: 0.75rem !important;
    }

    .shepherd-title {
      font-size: 0.95rem !important;
    }

    .shepherd-text {
      padding: 0.75rem !important;
      font-size: 0.85rem !important;
      line-height: 1.4 !important;
    }

    .shepherd-footer {
      padding: 0.5rem 0.75rem !important;
    }

    .shepherd-button {
      font-size: 0.8rem !important;
      padding: 0.4rem 0.75rem !important;
    }
  }

  .shepherd-header {
    background: #1f2937 !important;
    padding: 1rem;
    border-bottom: 1px solid #4b5563;
    border-radius: 8px 8px 0 0;
  }

  .shepherd-title {
    color: #fff;
    font-weight: bold;
    font-size: 1.125rem;
    margin: 0;
    max-width: 100%;
  }

  .shepherd-text {
    color: #d1d5db;
    padding: 1rem;
    font-size: 0.95rem;
    line-height: 1.6;
  }

  .shepherd-footer {
    padding: 0.75rem 1rem;
    border-top: 1px solid #4b5563;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .shepherd-button {
    background: #6366f1;
    border: none;
    border-radius: 6px;
    color: white;
    cursor: pointer;
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
    transition: background 0.2s;
    font-weight: 500;
  }

  .shepherd-button:hover {
    background: #5558e3;
  }

  .shepherd-button-secondary {
    background: transparent;
    border: 1px solid #4b5563;
    color: #d1d5db;
  }

  .shepherd-button-secondary:hover {
    background: #374151;
  }

  .shepherd-cancel-icon {
    color: #9ca3af;
    transition: color 0.2s;
  }

  .shepherd-cancel-icon:hover {
    color: #fff;
  }

  .shepherd-arrow:before {
    background: #111827;
    border: 1px solid #4b5563;
  }

  .shepherd-progress {
    color: #9ca3af;
    font-size: 0.85rem;
    margin: 0;
  }

  .shepherd-element[data-popper-placement^='top'] .shepherd-arrow:before {
    border-top-color: #111827;
  }

  .shepherd-element[data-popper-placement^='bottom'] .shepherd-arrow:before {
    border-bottom-color: #111827;
  }

  .shepherd-element[data-popper-placement^='left'] .shepherd-arrow:before {
    border-left-color: #111827;
  }

  .shepherd-element[data-popper-placement^='right'] .shepherd-arrow:before {
    border-right-color: #111827;
  }

  .shepherd-modal-overlay-container {
    opacity: 0.5;
  }
`;

// Section 1: Dashboard with NO subjects (welcome + add subject)
export const dashboardNoSubjectsSteps: TourStepConfig[] = [
  {
    id: 'welcome',
    context: 'dashboard',
    section: 'dashboard-no-subjects',
    title: 'Welcome to educAIte! 🎓',
    text: getResponsiveText(
      `Let's get you started with your first AI-powered lesson. This quick tour will show you everything you need to know.`,
      `Let's get you started with your first AI-powered lesson. This quick tour will show you everything you need to know.`
    ),
    buttons: [
      {
        text: 'Skip All Tours',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.cancel();
        }
      },
      {
        text: 'Next →',
        action(this: any) {
          this.next();
        }
      }
    ]
  },
  {
    id: 'add-subject-button',
    context: 'dashboard',
    section: 'dashboard-no-subjects',
    attachTo: {
      element: '[data-tour="add-subject-button"]',
      on: 'top'
    },
    title: 'Step 1: Add a Subject',
    text: getResponsiveText(
      'First, let\'s add a subject you want to learn. Tap the "Add New Subject" button below to continue.',
      'First, let\'s add a subject you want to learn. Click the "Add New Subject" button below to continue!'
    ),
    buttons: [
      {
        text: '← Back',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.back();
        }
      },
      {
        text: 'Got it!',
        action(this: any) {
          // Just complete this tour section - user will click button manually
          this.complete();
        }
      }
    ],
    advanceOn: { selector: '[data-tour="add-subject-button"]', event: 'click' }
  }
];

// Section 3: Dashboard WITH subjects (start lesson prompt)
export const dashboardWithSubjectsSteps: TourStepConfig[] = [
  {
    id: 'start-lesson-button',
    context: 'dashboard',
    section: 'dashboard-with-subjects',
    attachTo: {
      element: '[data-tour="start-lesson-button"]',
      on: 'top'
    },
    title: 'Start Your First Lesson',
    text: getResponsiveText(
      'Tap "Start New Lesson" below to begin learning!',
      'Tap "Start New Lesson" below to begin learning!'
    ),
    buttons: [
      {
        text: 'Got it!',
        action(this: any) {
          // Just complete the tour - let user click the button themselves
          this.complete();
        }
      }
    ],
    advanceOn: { selector: '[data-tour="start-lesson-button"]', event: 'click' }
  }
];

// Section 2: Add Subject Page (form explanations)
export const addSubjectSteps: TourStepConfig[] = [
  {
    id: 'subject-form',
    context: 'add-subject',
    section: 'add-subject',
    title: 'Fill in Subject Details',
    text: getResponsiveText(
      `Here you can add the subjects you need help with. Just fill out the form and make sure to click on the info icons for clarification, if needed.`,
      `Here you can add the subjects you need help with. Just fill out the form and make sure to click on the info icons for clarification, if needed.`
    ),
    buttons: [
      {
        text: 'Got it! →',
        action(this: any) {
          this.complete();
        }
      }
    ],
    advanceOn: { selector: 'input[name="subject_name"]', event: 'keydown' }
  }
];

// Section 4: Lesson Page General (image upload, history, support)
export const lessonGeneralSteps: TourStepConfig[] = [
  {
    id: 'lesson-overview',
    context: 'lesson',
    section: 'lesson-general',
    title: 'Your Learning Space',
    text: getResponsiveText(
      `This is where the magic happens! You can interact with your AI tutor in the mode you selected. Just start speaking or typing when ready.`,
      `This is where the magic happens! You can interact with your AI tutor in the mode you selected. Just start speaking or typing when ready.`
    ),
    buttons: [
      {
        text: 'Next →',
        action(this: any) {
          this.next();
        }
      }
    ]
  },
  {
    id: 'image-upload',
    context: 'lesson',
    section: 'lesson-general',
    attachTo: {
      element: '[data-tour="image-upload"]',
      on: 'top'
    },
    title: 'Upload Images',
    text: getResponsiveText(
      `Got a problem from your textbook? Practice problems you want to work on? Just tap this button to upload images. Your tutor will help you solve it.`,
      `Got a problem from your textbook? Practice problems you want to work on? Just click this button to upload an image OR copy & paste images directly into the chat! Your tutor help you solve it.`
    ),
    buttons: [
      {
        text: '← Back',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.back();
        }
      },
      {
        text: 'Next →',
        action(this: any) {
          this.next();
        }
      }
    ]
  },
  {
    id: 'support-button',
    context: 'lesson',
    section: 'lesson-general',
    attachTo: {
      element: '[data-tour="support-button"]',
      on: 'left'
    },
    title: 'Need Help or Have Ideas?',
    text: getResponsiveText(
      `Use the support option in the menu for any questions or suggestions you have. We read everything and love hearing from you!`,
      `See the support button? You can use it for any questions or even suggestions you have. We read everything and love hearing from you!`
    ),
    mobileConfig: {
      modalWidth: 260,
      modalOffset: { top: 220 },
      arrowOffset: { left: 120 },
      keepDefaultLeft: true,
      keepDefaultTransform: true,
    },
    buttons: [
      {
        text: '← Back',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.back();
        }
      },
      {
        text: 'Next →',
        action(this: any) {
          this.next();
        }
      }
    ],
    when: {
      show(this: any) {
        // Open mobile menu when showing this step on mobile
        const event = new CustomEvent('tour:open-mobile-menu');
        window.dispatchEvent(event);
      },
      hide(this: any) {
        // Close mobile menu when leaving this step
        const event = new CustomEvent('tour:close-mobile-menu');
        window.dispatchEvent(event);
      }
    }
  },
  {
    id: 'lesson-history',
    context: 'lesson',
    section: 'lesson-general',
    attachTo: {
      element: '[data-tour="lesson-history-button"]',
      on: 'left'
    },
    title: 'Review Your Lessons',
    text: getResponsiveText(
      `After you end a lesson, you can always review the entire conversation in Lesson History. Everything is saved so you can review later!`,
      `After you end a lesson, you can always review the entire conversation in Lesson History. Everything is saved so you can review later!`
    ),
    mobileConfig: {
      modalWidth: 260,
      arrowOffset: { left: -150 },
      modalOffset: { top: 230 },
      keepDefaultLeft: true,
      keepDefaultTransform: true,
    },
    buttons: [
      {
        text: '← Back',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.back();
        }
      },
      {
        text: 'Got it! 🎉',
        action(this: any) {
          // Close mobile menu when completing
          const closeEvent = new CustomEvent('tour:close-mobile-menu');
          window.dispatchEvent(closeEvent);
          this.complete();
        }
      }
    ],
    when: {
      show(this: any) {
        // Open mobile menu when showing this step on mobile
        const event = new CustomEvent('tour:open-mobile-menu');
        window.dispatchEvent(event);
      },
      hide(this: any) {
        // Close mobile menu when leaving this step
        const event = new CustomEvent('tour:close-mobile-menu');
        window.dispatchEvent(event);
      }
    }
  }
];

// Section 5: Voice Mode (stop recording - shown when call connects)
export const lessonVoiceSteps: TourStepConfig[] = [
  {
    id: 'stop-recording',
    context: 'lesson',
    section: 'lesson-voice',
    attachTo: {
      element: '[data-tour="stop-recording"]',
      on: 'top'
    },
    title: '⚠️ Voice Mode: Stop Recording',
    text: getResponsiveText(
      `IMPORTANT: When you finish speaking, you MUST tap the Stop Recording button that appears at the bottom. Otherwise, the tutor won't know you're done and can't respond.`,
      `IMPORTANT: When you finish speaking, you MUST press the Stop Recording button that appears. Otherwise, the AI won't know you're done and can't respond.`
    ),
    buttons: [
      {
        text: 'Got it!',
        action(this: any) {
          this.complete();
        }
      }
    ],
    advanceOn: { selector: '[data-tour="stop-recording"]', event: 'click' }
  }
];

// Section 6: PDF/Lesson Context (NEW - shown to all users, even if tours were skipped)
export const lessonPdfContextSteps: TourStepConfig[] = [
  {
    id: 'right-panel-handle',
    context: 'lesson',
    section: 'lesson-pdf-context',
    title: 'Lesson Context Panel',
    mobileOnly: true, // Only show this step on mobile - desktop users can see the panel already
    attachTo: {
      element: '[data-tour="right-panel-handle"]',
      on: 'right'
    },
    text: getResponsiveText(
      `See that tab on the right edge of your screen? Tap it to access tools to provide context for your lessons. Let's open it now.`,
      `See that **Lesson Info Panel** on the right side?\n\nIt contains:\n• Lesson details\n• Context materials (PDFs you upload)\n• Upload new materials\n\nLet's take a tour!`
    ),
    mobileConfig: {
      modalWidth: 260,
    },
    buttons: [
      {
        text: 'Skip Tour',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.cancel();
        }
      },
      {
        text: 'Show Me! →',
        action(this: any) {
          this.next();
        }
      }
    ],
    when: {
      show(this: any) {
        // Trigger bounce animation on handle
        const event = new CustomEvent('tour:bounce-handle');
        window.dispatchEvent(event);
      }
    }
  },
  {
    id: 'pdf-context-intro',
    context: 'lesson',
    section: 'lesson-pdf-context',
    title: 'New Feature: Lesson Context & PDFs',
    text: getResponsiveText(
      `We've added a powerful new feature to help you learn more effectively! You can now upload PDFs to provide the tutor with context for your lessons.`,
      `We've added a powerful new feature to help you learn more effectively! You can now upload PDFs (worksheets, syllabi, study guides) and the AI tutor will use them as context for your lessons.`
    ),
    mobileConfig: {
      modalWidth: 260,
    },
    when: {
      show(this: any) {
        // Open right panel when showing this step
        const event = new CustomEvent('tour:open-right-panel');
        window.dispatchEvent(event);
      }
    },
    buttons: [
      {
        text: 'Skip Tour',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.cancel();
        }
      },
      {
        text: 'Show Me! →',
        action(this: any) {
          this.next();
        }
      }
    ]
  },
  {
    id: 'pdf-upload-button',
    context: 'lesson',
    section: 'lesson-pdf-context',
    attachTo: {
      element: '[data-tour="pdf-upload-button"]',
      on: 'bottom'
    },
    title: 'Upload PDFs',
    text: getResponsiveText(
      `You can click on this to upload the PDF files such as: Homework worksheets, Course syllabi, Study guides and practice problems. The AI tutor will extract the information and you can refer to it during your lesson.`,
      `You can click on this to upload the PDF files such as: Homework worksheets, Course syllabi, Study guides and practice problems. The AI tutor will extract the information and you can refer to it during your lesson.`
    ),
    mobileConfig: {
      modalWidth: 260,
    },
    buttons: [
      {
        text: '← Back',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.back();
        }
      },
      {
        text: 'Next →',
        action(this: any) {
          this.next();
        }
      }
    ],
    when: {
      show(this: any) {
        // Open right panel when showing this step on mobile
        const event = new CustomEvent('tour:open-right-panel');
        window.dispatchEvent(event);
      }
    }
  },
  {
    id: 'lesson-context-panel',
    context: 'lesson',
    section: 'lesson-pdf-context',
    attachTo: {
      element: '[data-tour="lesson-context-panel"]',
      on: 'top'
    },
    title: 'Lesson Context Panel',
    text: getResponsiveText(
      `After uploading, your lesson context and uploaded materials can be seen here, for reference.`,
      `After uploading, your lesson context and uploaded materials appear here for reference during your lesson.`
    ),
    mobileConfig: {
      modalWidth: 260,
    },
    buttons: [
      {
        text: '← Back',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.back();
        }
      },
      {
        text: 'Next →',
        action(this: any) {
          this.next();
        }
      }
    ],
    when: {
      show(this: any) {
        // Open right panel when showing this step on mobile
        const event = new CustomEvent('tour:open-right-panel');
        window.dispatchEvent(event);
      },
      hide(this: any) {
        // Close right panel when leaving this step
        const event = new CustomEvent('tour:close-right-panel');
        window.dispatchEvent(event);
      }
    }
  },
  {
    id: 'how-ai-uses-context',
    context: 'lesson',
    section: 'lesson-pdf-context',
    title: 'How the AI Uses Your Materials',
    text: getResponsiveText(
      `When you upload the PDFs, your tutor will read and understand what it is you want to learn during this lesson. Feel free to refer to the PDFs directly during your lesson. For example, tell it to "help me with question 3" or "explain what is said on page 5". Happy learning!`,
      `When you upload the PDFs, your tutor will read and understand what it is you want to learn during this lesson. Feel free to refer to the PDFs directly during your lesson. For example, tell it to "help me with question 3" or "explain what is said on page 5". Happy learning!`
    ),
    buttons: [
      {
        text: '← Back',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.back();
        }
      },
      {
        text: 'Done',
        action(this: any) {
          this.next();
        }
      }
    ]
  },
];

// Child Account Tours

// Child with NO subjects
export const childNoSubjectsSteps: TourStepConfig[] = [
  {
    id: 'child-welcome',
    context: 'dashboard',
    section: 'child-no-subjects',
    title: 'Welcome to educAIte! 🎓',
    text: getResponsiveText(
      `Hi there! Before we can begin any lessons, you'll need to ask your parent or guardian to add subjects for you. Afterwards, come back and we can start learning.`,
      `Hi there! Before we can begin any lessons, you'll need to ask your parent or guardian to add subjects for you. Afterwards, come back and we can start learning.`
    ),
    buttons: [
      {
        text: 'Got it!',
        action(this: any) {
          this.complete();
        }
      }
    ]
  }
];

// Child WITH subjects
export const childWithSubjectsSteps: TourStepConfig[] = [
  {
    id: 'child-start-lesson',
    context: 'dashboard',
    section: 'child-with-subjects',
    attachTo: {
      element: '[data-tour="start-lesson-button"]',
      on: 'top'
    },
    title: 'Ready to Learn! 🚀',
    text: getResponsiveText(
      `Welcome! Tap "Start New Lesson" below whenever you're ready to begin your first lesson.`,
      `Welcome! Tap "Start New Lesson" below whenever you're ready to begin your first lesson.`
    ),
    buttons: [
      {
        text: 'Got it!',
        action(this: any) {
          this.complete();
        }
      }
    ],
    advanceOn: { selector: '[data-tour="start-lesson-button"]', event: 'click' }
  }
];

// Parent Account Tours

// Parent with NO children
export const parentNoChildrenSteps: TourStepConfig[] = [
  {
    id: 'parent-welcome',
    context: 'dashboard',
    section: 'parent-no-children',
    title: 'Welcome to the educAIte Parent Dashboard!',
    text: getResponsiveText(
      `Welcome! This is your parent dashboard where you can manage your children's learning experience. From here you can manage the subjects your children are getting tutoring for and can check on their lessons too. Let's get started!`,
      `Welcome! This is your parent dashboard where you can manage your children's learning experience. From here you can manage the subjects your children are getting tutoring for and can check on their lessons too. Let's get started!`
    ),
    buttons: [
      {
        text: 'Skip Tour',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.cancel();
        }
      },
      {
        text: 'Next →',
        action(this: any) {
          this.next();
        }
      }
    ]
  },
  {
    id: 'parent-add-child',
    context: 'dashboard',
    section: 'parent-no-children',
    attachTo: {
      element: '[data-tour="add-child-button"]',
      on: 'top'
    },
    title: 'Step 1: Add a Child Account',
    text: getResponsiveText(
      `First, let's create an account for your child. Tap the "Add Child" button below to create their learning profile. You will need a valid email address for them to log in with.`,
      `First, let's create an account for your child. Click the "Add Child" button to create their learning profile. You will need a valid email address for them to log in with.`
    ),
    buttons: [
      {
        text: '← Back',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.back();
        }
      },
      {
        text: 'Got it!',
        action(this: any) {
          this.complete();
        }
      }
    ]
  }
];

// Parent WITH children
export const parentWithChildrenSteps: TourStepConfig[] = [
  {
    id: 'parent-manage-subjects',
    context: 'dashboard',
    section: 'parent-with-children',
    attachTo: {
      element: '[data-tour="manage-subjects-button"]',
      on: 'top'
    },
    title: 'Step 2: Add Subjects',
    text: getResponsiveText(
      `Great! You've added a child account. Tap "Manage Subjects" for each child to add the subjects they need help with. You can add as many as needed, and even upload their syllabus to help the AI tutor personalize lessons.`,
      `Great! You've added a child account. Now click "Manage Subjects" for your child to add the subjects they need help with. You can add as many as needed, and even upload their syllabus to help the AI tutor personalize lessons.`
    ),
    buttons: [
      {
        text: 'Next →',
        action(this: any) {
          this.next();
        }
      }
    ]
  },
  {
    id: 'parent-view-history',
    context: 'dashboard',
    section: 'parent-with-children',
    attachTo: {
      element: '[data-tour="view-history-button"]',
      on: 'top'
    },
    title: 'Step 3: Monitor Progress',
    text: getResponsiveText(
      `Once your child starts taking lessons, you can tap "View History" to see their complete lesson history. You'll have full visibility into their learning journey.`,
      `Once your child starts taking lessons, you can click "View History" to see their complete lesson history. You'll have full visibility into their learning journey.`
    ),
    buttons: [
      {
        text: '← Back',
        classes: 'shepherd-button-secondary',
        action(this: any) {
          this.back();
        }
      },
      {
        text: 'All Set! 🎉',
        action(this: any) {
          this.complete();
        }
      }
    ]
  }
];

// Progress footer template
export function createProgressFooter(currentStep: number, totalSteps: number): string {
  return `
    <div class="shepherd-progress">
      Step ${currentStep} of ${totalSteps}
    </div>
  `;
}
